import { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { ChevronRightIcon, X } from 'lucide-react';
import { useLocation } from 'react-router';
import { useTranslations } from '@/lib/i18n/useTranslations';
import { buildNavigationRoutes } from './navigation.routes';
import { usePermissions } from '@/modules/users/hooks/usePermissions';
import { cn } from '@/lib/utils/cn';
import { useBrandStore } from '@/lib/store/brand.store';
import { BrandLogo } from '@/modules/settings/brand/components/BrandLogo';

interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    name?: string;
    email?: string;
    avatarUrl?: string;
  } | null;
  onLogout: () => void;
}

export const MobileNavigation = ({ isOpen, onClose, user, onLogout }: MobileNavigationProps) => {
  const { pathname } = useLocation();
  const { canRead } = usePermissions();
  const t = useTranslations('Navigation');
  const brand = useBrandStore(s => s.brand);
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const visibleNavigation = buildNavigationRoutes(pathname, {
    dashboard: t('dashboard'),
    services: t('services'),
    incidences: t('incidences'),
    inventory: t('inventory'),
    products: t('products'),
    stock: t('stock'),
    consolidatedStock: t('consolidatedStock'),
    serialNumbers: t('serialNumbers'),
    users: t('users'),
    customers: t('customers'),
    billing: t('billing'),
    quotes: t('quotes'),
    invoices: t('invoices'),
    deliveryNotes: t('deliveryNotes'),
    accounting: t('accounting'),
    purchasing: t('purchasing'),
    suppliers: t('suppliers'),
    purchaseInvoices: t('purchaseInvoices'),
    workOrders: t('workOrders'),
    calendar: t('calendar'),
    finance: t('finance'),
    accountsPayable: t('accountsPayable'),
  }).filter(item => {
    if (canRead(item.module)) {
      return true;
    }
    if (item.children?.length) {
      return item.children.some(child => canRead(child.module));
    }
    return false;
  });

  // The drawer is a modal dialog: focus moves into it on open and returns to the
  // invoking control on close.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    drawerRef.current?.focus();

    return () => {
      previousFocusRef.current?.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !drawerRef.current) {
        return;
      }

      const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        return;
      }

      // Keep Tab cycling inside the modal drawer.
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('menu')}
        tabIndex={-1}
        className="absolute left-0 top-0 flex h-full w-64 flex-col bg-white pt-3 shadow-xl focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 pb-4">
          <Link to="/dashboard" onClick={onClose} className="min-w-0 max-w-[180px]">
            <BrandLogo
              name={brand?.name || brand?.legalName}
              logoUrl={brand?.logoUrl}
              variant="mobile"
            />
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-50 p-1 text-slate-600 hover:bg-slate-100"
          >
            <X strokeWidth={1.5} className="h-5 w-5" />
            <span className="sr-only">{t('closeMenu')}</span>
          </button>
        </div>

        {/* Navigation items */}
        <div className="flex-1 overflow-y-auto">
          <ul>
            {visibleNavigation.map(item => {
              const hasChildren = item.children?.length;

              if (!hasChildren) {
                return (
                  <li key={item.name} className="px-2">
                    <Link
                      to={item.href ?? '#'}
                      onClick={onClose}
                      className={cn(
                        'flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-50',
                        item.isActive && 'bg-slate-100 font-semibold text-slate-900',
                      )}
                    >
                      <item.icon strokeWidth={1.5} />
                      {item.name}
                    </Link>
                  </li>
                );
              }

              const visibleChildren = item.children?.filter(child => canRead(child.module)) ?? [];

              return (
                <section key={item.name}>
                  {item.separator === 'top' && <div className="border-t border-slate-200 p-2" />}
                  <li className="px-2">
                    <div
                      className={cn(
                        'flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700',
                        item.isActive && 'bg-slate-50',
                      )}
                    >
                      <item.icon strokeWidth={1.5} />
                      <span className="flex-1 text-left font-medium">{item.name}</span>
                      {visibleChildren.length > 0 && (
                        <ChevronRightIcon className="h-4 w-4 rotate-90" />
                      )}
                    </div>
                    {visibleChildren.length > 0 && (
                      <ul className="mt-1 space-y-1 pl-9">
                        {visibleChildren.map(child => (
                          <li key={child.name}>
                            <Link
                              to={child.href ?? '#'}
                              onClick={onClose}
                              className={cn(
                                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50',
                                child.isActive && 'bg-slate-100 text-slate-900 font-semibold',
                              )}
                            >
                              <child.icon strokeWidth={1.5} />
                              {child.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                </section>
              );
            })}
          </ul>
        </div>

        {/* Footer */}
        {user && (
          <div className="border-t border-slate-200 p-3">
            <div className="flex items-center gap-3">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-600">
                  {(user.name ?? user.email ?? '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">
                  {user.name ?? user.email}
                </p>
              </div>
              <button
                type="button"
                onClick={onLogout}
                className="rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
              >
                {t('logout')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
